/**
 * Acquisition attribution: the ad clicks and campaign links that preceded a
 * sign-in, used to count sign-ups per campaign and to tell Google Ads which
 * clicks converted. Nothing else.
 *
 * Every value here comes from the visitor's browser, okthx.com's landing POST
 * or links, or the app, and anyone can write any value. So it is never an identity:
 * nothing may use it to find, match or merge a person, user, account or lead,
 * to carry data onto an account, or to decide access, billing or the waitlist.
 * It is recorded against the account that just signed in, which the server
 * already knows from its own session, never one found through these values.
 * Adding a lead id or any other lookup key to these shapes breaks that rule.
 */

import { z } from "zod";

/** Where a sign-in's recorded touch came from. */
export const ACQUISITION_KINDS = ["google_ads", "apple_ads", "utm"] as const;
export type AcquisitionKind = (typeof ACQUISITION_KINDS)[number];

/** Google's click ids, in the order a conversion upload prefers them. */
export const CLICK_ID_TYPES = ["gclid", "gbraid", "wbraid"] as const;
export type ClickIdType = (typeof CLICK_ID_TYPES)[number];

/** The query parameters a landing captures. Any other key is ignored. */
export const ATTRIBUTION_PARAM_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "gad_source",
  "gad_campaignid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
] as const;

const clickId = z.string().regex(/^[A-Za-z0-9_-]{1,200}$/);
const utmValue = z
  .string()
  .trim()
  .min(1)
  .max(100)
  // biome-ignore lint/suspicious/noControlCharactersInRegex: rejecting control characters is the point
  .regex(/^[^\u0000-\u001f\u007f]*$/);

/** A bad field drops alone rather than failing the whole capture. */
const optional = <T extends z.ZodType>(schema: T) => schema.optional().catch(undefined);

export const attributionParams = z
  .object({
    gclid: optional(clickId),
    gbraid: optional(clickId),
    wbraid: optional(clickId),
    gad_source: optional(z.string().regex(/^\d{1,8}$/)),
    gad_campaignid: optional(z.string().regex(/^\d{1,20}$/)),
    utm_source: optional(utmValue),
    utm_medium: optional(utmValue),
    utm_campaign: optional(utmValue),
    utm_term: optional(utmValue),
    utm_content: optional(utmValue),
    utm_id: optional(utmValue),
  })
  .catch({});
export type AttributionParams = z.infer<typeof attributionParams>;

/** Body of `POST /api/p/attribution`, sent by the web app when a page loads with campaign parameters. */
export const attributionCaptureReq = z.object({
  params: attributionParams,
  /** The landing path, without its query or fragment. */
  landingPage: optional(
    z
      .string()
      .max(200)
      .regex(/^\/[^?#]*$/),
  ),
  /** The page that linked here; the server keeps only its origin. */
  referrer: optional(z.string().url().max(200)),
});
export type AttributionCaptureReq = z.infer<typeof attributionCaptureReq>;

/** One touch inside an attribution cookie. `at` is epoch milliseconds. */
export const storedTouch = z.object({
  at: z.number().int().positive(),
  p: attributionParams,
  /** Landing path. */
  lp: optional(z.string().max(200)),
  /** Referrer origin. */
  rf: optional(z.string().max(100)),
});
export type StoredTouch = z.infer<typeof storedTouch>;

/**
 * `bbl_attr`, set by the API as base64url JSON: the newest web touch of each
 * kind this browser has seen.
 */
export const attributionCookie = z.object({
  v: z.literal(1),
  google_ads: optional(storedTouch),
  utm: optional(storedTouch),
});
export type AttributionCookie = z.infer<typeof attributionCookie>;

/** The web touches a browser can carry; Apple touches arrive from the app instead. */
export type WebTouches = { google_ads?: StoredTouch; utm?: StoredTouch };

const appleId = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).optional();
const appleText = z.string().max(100).optional();

/**
 * Body of `POST /api/a/attribution/apple-ads`: Apple's AdServices attribution
 * response, as the app received it.
 *
 * `conversionType` and `claimType` take any short string, so a value Apple
 * adds later doesn't fail the whole report.
 */
export const appleAdsAttributionReq = z.object({
  attribution: z.boolean(),
  orgId: appleId,
  campaignId: appleId,
  adGroupId: appleId,
  keywordId: appleId,
  adId: appleId,
  conversionType: appleText,
  claimType: appleText,
  clickDate: appleText,
  impressionDate: appleText,
  countryOrRegion: appleText,
  supplyPlacement: appleText,
});
export type AppleAdsAttributionReq = z.infer<typeof appleAdsAttributionReq>;
